import React, { useEffect, useState } from 'react'
import { components, constants, useServices, useTranslation } from 'cng-web-lib'
import NaCodeMaintenanceApiURLs from 'src/apiUrls/NaCodeMaintenanceApiUrls'
import TrailerDTODialog from './TrailerDTODialog'
import {
  Box,
  Grid,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import ACIHighwayManifestTranslationText from './../contexts/ACIHighwayManifestTranslationText'

const useStyles = makeStyles((theme) => ({
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: theme.palette.background.sectionOddBg,
      '& .MuiTableCell-head': {
        borderBottom: 0,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        textTransform: 'uppercase'
      }
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        '&:last-child': {
          '& .MuiTableCell-root': {
            borderBottom: 0
          }
        }
      }
    },
    '& .MuiTableCell-root': {
      padding: '8px 16px'
    }
  }
}))

const {
  button: { CngButton, CngIconButton },
  table: { useFetchCodeMaintenanceLookup }
} = components

const {
  filter: { EQUAL }
} = constants

function TrailerDTOTable(props) {
  const {
    data,
    onAddTrailerDTO,
    onDeleteTrailerDTO,
    onEditTrailerDTO,
    showNotification
  } = props

  const [lookups, setLookups] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [trailerDTODialog, setTrailerDTODialog] = useState({
    isView: false,
    open: false,
    trailerDTO: null
  })
  const { fetchRecords } = useServices()
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const aciHighwayManifestTranslationText = ACIHighwayManifestTranslationText() 
  const classes = useStyles()

  const filtered =
    searchTerm !== ''
      ? data.filter((trailerDTO) =>
          trailerDTO.equipmentNumber
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      : data

  useEffect(() => {
    Promise.all([
      // Cargo loaded in
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'ACIHWY_TRAILER_TYPE' }],
        undefined,
        'code'
      ),
      // Countries
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [
          {
            field: 'codeType',
            operator: EQUAL,
            value: 'ACIHWY_MANIFEST_COUNTRY'
          }
        ],
        undefined,
        'code'
      ),
      // Country states
      fetchRecords.execute(
        NaCodeMaintenanceApiURLs.GET,
        {
          filters: [{ field: 'indicator', operator: EQUAL, value: 'ACI' }],
          customData: { codeMType: 'CountryStateCode' }
        },
        (res) => res.content
      ),
      // Exemption cargo
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'ACIHWY_EXEMPTION_CARGO' }],
        undefined,
        'code'
      )
    ]).then(
      ([
        cargoLoadedIn,
        countries,
        countryStates,
        exemptionCargo
      ]) => {
        const states = Object.keys(countries).reduce((acc, countryCode) => {
          acc[countryCode] = countryStates.filter(
            (state) => state.code === countryCode
          )

          return acc
        }, {})

        setLookups({
          cargoLoadedIn,
          countries,
          countryStates: states,
          exemptionCargo
        })
      }
    )
  }, [])

  function getLookupValue(name, value) {
    if (!lookups) return value

    return lookups[name] && lookups[name][value] ? lookups[name][value] : value
  }

  function getCountryStateLabel(country, state) {
    if (!lookups || !country || !state) return state

    // Check if country code exists in the lookup
    if (Object.keys(lookups.countries).includes(country)) {
      const result = lookups.countryStates[country].find(
        (countryState) => countryState.intlcode === state
      )

      return result ? result.descriptionEn : state
    } else {
      return state
    }
  }

  return (
    <>
      <Box marginBottom={1}>
        <Grid container spacing={2}>
          <Grid item xs>
            {data.length > 0 && (
              <TextField
                fullWidth
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={aciHighwayManifestTranslationText.searchInYourEntry}
                size='small'
                variant='outlined'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <FontAwesomeIcon icon={['fal', 'search']} />
                    </InputAdornment>
                  )
                }}
              />
            )}
          </Grid>
          {onAddTrailerDTO && (
            <Grid item xs='auto'>
              <CngButton
                color='secondary'
                onClick={() =>
                  setTrailerDTODialog({
                    open: true,
                    isView: false,
                    trailerDTO: null
                  })
                }
                size='medium'
                startIcon={<FontAwesomeIcon icon={['fal', 'plus-circle']} />}
              >
                {aciHighwayManifestTranslationText.addEquipment}
              </CngButton>
            </Grid>
          )}
        </Grid>
      </Box>
      <TableContainer>
        <Table className={clsx(classes.table, 'new-ui-table-component__table')}>
          <TableHead>
            <TableRow>
              <TableCell>{aciHighwayManifestTranslationText.equipmentNumber}</TableCell>
              <TableCell>{aciHighwayManifestTranslationText.cargoLoadedIn}</TableCell>
              <TableCell>{aciHighwayManifestTranslationText.licensePlateNum}</TableCell>
              {(onEditTrailerDTO || onDeleteTrailerDTO) && (
                <TableCell align='right'>{aciHighwayManifestTranslationText.action}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length > 0 ? (
              filtered.map((trailerDTO) => (
                <TableRow
                  key={Math.random()}
                  onClick={() =>
                    setTrailerDTODialog({ open: true, isView: true, trailerDTO })
                  }
                >
                  <TableCell>{trailerDTO.equipmentNumber}</TableCell>
                  <TableCell>
                    {getLookupValue('cargoLoadedIn', trailerDTO.cargoLoadedIn)}
                  </TableCell>
                  <TableCell>{trailerDTO.licensePlateNum}</TableCell>
                  {(onEditTrailerDTO || onDeleteTrailerDTO) && (
                    <TableCell align='right'>
                      <Grid container justify='flex-end' spacing={1}>
                        {onEditTrailerDTO && (
                          <Grid item xs='auto'>
                            <CngIconButton
                              type='outlined'
                              icon={['fal', 'pen']}
                              onClick={(event) => {
                                event.stopPropagation()
                                setTrailerDTODialog({ open: true, isView: false, trailerDTO })
                              }}
                              size='small'
                            />
                          </Grid>
                        )}
                        {onDeleteTrailerDTO && (
                          <Grid item xs='auto'>
                            <CngIconButton
                              type='outlined'
                              icon={['fal', 'trash']}
                              onClick={(event) => {
                                event.stopPropagation()
                                onDeleteTrailerDTO(trailerDTO)
                              }}
                              size='small'
                            />
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  align='center'
                  colSpan={3 + (onEditTrailerDTO || onDeleteTrailerDTO ? 1 : 0)}
                >
                  <Typography variant='body2' color='textSecondary'>
                    {aciHighwayManifestTranslationText.noRecordsToDisplay}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TrailerDTODialog
        getLookupValue={getLookupValue}
        getCountryStateLabel={getCountryStateLabel}
        isView={trailerDTODialog.isView}
        open={trailerDTODialog.open}
        onClose={() =>
          setTrailerDTODialog({ open: false, isView: false, trailerDTO: null })
        }
        onAddTrailerDTO={onAddTrailerDTO}
        onEditTrailerDTO={onEditTrailerDTO}
        showNotification={showNotification}
        trailerDTO={trailerDTODialog.trailerDTO}
      />
    </>
  )
}

export default TrailerDTOTable
